import type { FieldErrors } from "@octopusdeploy/octopus-react-client";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import { useCallback, useState } from "react";
import { repository } from "~/clientInstance";
export type UseValidatingModelReturnValue<TModel> = {
    model: TModel;
    setModel: (model: Partial<TModel>) => void;
    validate: () => Promise<boolean>;
    getFieldError: (fieldName: keyof TModel & string) => string | undefined;
    reset: () => void;
};
export type UseValidatingModelConfiguration<TModel> = {
    initialModel: TModel;
    onValidate: (repository: Repository, modelToValidate: TModel) => Promise<FieldErrors | undefined>;
};
export function useValidatingModel<TModel>(configuration: UseValidatingModelConfiguration<TModel>): UseValidatingModelReturnValue<TModel> {
    const { initialModel, onValidate } = configuration;
    const [model, setModel] = useState<TModel>(initialModel);
    const [cleanModel] = useState<TModel>(initialModel);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const updateModel = useCallback((partial: Partial<TModel>) => setModel((prev) => ({ ...prev, ...partial })), [setModel]);
    const validate = useCallback(async () => {
        //first step is to clear the previous errors
        setFieldErrors({});
        //we just use the global repository instance for now
        const newErrors = (await onValidate(repository, model)) || {};
        setFieldErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [model, onValidate]);
    const getFieldError = useCallback((fieldName: keyof TModel & string) => fieldErrors[fieldName], [fieldErrors]);
    const reset = useCallback(() => {
        setFieldErrors({});
        setModel(cleanModel);
    }, [cleanModel]);
    return {
        model,
        setModel: updateModel,
        validate,
        getFieldError,
        reset,
    };
}
